/**
 * Wizard — shared primitives.
 *
 * Phase 27 refactor — extracted verbatim from the original DealWizard.jsx so
 * downstream step components can import without circular dependencies.
 * No behavior changes. Don't refactor further without a UX diff.
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, ChevronDown, Database, Loader2, ScrollText, ShieldCheck } from "lucide-react";
import { Check } from "lucide-react";

// Phase 17 — Rental-strategy options used in the segmented control on the
// Property step and as the parameter for the regulation lookup.
export const RENTAL_STRATEGIES = [
  { id: "long_term", label: "Long-Term", hint: "12+ month lease — standard residential." },
  { id: "short_term", label: "Short-Term", hint: "Airbnb / VRBO / nightly — STR rules apply." },
  { id: "mid_term", label: "Mid-Term", hint: "30–180 day corporate / travel-nurse stays." },
];

// Phase 17 — Pretty labels for facts.tax_source returned by /market/property-lookup.
export const TAX_SOURCE_LABEL = {
  attom: { label: "Estimated from ATTOM Data", tone: "indigo", icon: Database },
  county: { label: "Estimated from county tax rate", tone: "indigo", icon: Database },
  national_median_fallback: { label: "Estimated from national median rate", tone: "amber", icon: Database },
  fallback: { label: "Estimated from market fallback data", tone: "amber", icon: Database },
};

export const STORAGE_KEY = "runidea_wizard_v1";
export const SQFT_PER_SQM = 10.7639;

export const DEFAULT_FORM = {
  property: {
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    property_type: "single_family",
    units: 1,
    bedrooms: 3,
    bathrooms: 2,
    square_feet: 1500,
    asking_price: 0,
  },
  analysis: {
    monthly_rent: 0,
    taxes_annual: 0,
    insurance_annual: 0,
    hoa_monthly: 0,
    utilities_monthly: 0,
    repairs: 0,
    vacancy_percent: 5,
    management_percent: 8,
    down_payment_percent: 25,
    interest_rate: 7,
    loan_term_years: 30,
    closing_costs: 0,
    transfer_tax: 0,
    // Phase 15 — Counter-Offer + Reverse-Yield engine.
    description: "",
    counter_offer_price: 0,
    // Phase 17 — Cash purchase + rental strategy
    is_cash_purchase: false,
    rental_strategy: "long_term",
    // Phase 19 — Owned-property tracking. is_owned=false hides everything else
    // and the historical-performance endpoint stays 400 (correct behaviour for
    // analyzing-only deals). The four sub-fields are only used when is_owned=true.
    is_owned: false,
    purchase_year: null,
    purchase_price_paid: 0,
    original_loan_amount: 0,
    original_interest_rate: 0,
    original_loan_term_years: 30,
    // Batch F — Realized Performance fields. Only used when is_owned=true.
    rental_start_year: null,
    annual_rent_growth_percent: 3.0,
    current_estimated_value: 0,
    current_value_source: "manual",
    current_value_updated_at: null,
    // Phase 36 Batch X — GMIA wizard Step 1.5 (currency config).
    // Same-currency deals never touch these. Cross-currency deals
    // either leave `did_exchange_funds=false` (server locks today's
    // rate at create) OR flip it true and supply the locked rate.
    did_exchange_funds: false,
    purchase_exchange_rate: 0,
    purchase_date: "",
    // Phase 36 Batch AA — Flip / BRRRR resale-strategy fields. These
    // ship with shape-stable defaults so:
    //   1. Step2Income's NumberFields are never bound to `undefined`
    //      (controlled-input warning + lost-on-blur regressions).
    //   2. JSON.stringify of the wizard draft preserves them, so the
    //      session-storage round-trip ALWAYS includes ARV/repair etc.
    //   3. Edit-mode hydration (in useWizardState) now has a real key
    //      to set, instead of relying on the form picking up a value
    //      that was never declared in DEFAULT_FORM.
    arv: 0,
    repair_budget: 0,
    holding_period_months: 6,
    holding_costs_monthly: 0,
    selling_costs_pct: 8,
    cash_purchase: false,
    refi_ltv_pct: 75,
    refi_interest_rate: 7,
    refi_loan_term_years: 30,
    refi_closing_costs: 0,
    stabilization_months: 6,
    // Phase 44 Batch BF1 — multi-year special assessments captured at
    // the deal level. Empty array on create; the wizard's expense step
    // exposes an "Add special assessment" CTA below HOA.
    special_assessments: [],
  },
  // Phase 36 — Listing & contact info. Top-level (not nested under
  // analysis or property) because backend persists it on the deal doc
  // itself, parallel to deal_type. listing_type defaults to "unknown"
  // so legacy drafts read back cleanly.
  contact: {
    listing_type: "unknown",
    contact_name: "",
    contact_brokerage: "",
    contact_email: "",
    contact_phone: "",
    contact_cell: "",
    contact_phone_same_as_cell: false,
    contact_notes: "",
    listing_url: "",
  },
};

export function readDraft() {
  // Lazy read of sessionStorage so we never race the restore-vs-persist effects.
  try {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const STEPS = [
  { id: 1, title: "Type", description: "Strategy" },
  { id: 2, title: "Property", description: "Where & what" },
  { id: 3, title: "Income", description: "Rent / resale" },
  { id: 4, title: "Expenses", description: "Taxes, ops & repairs" },
  { id: 5, title: "Financing", description: "Loan terms" },
  { id: 6, title: "Review", description: "Analyze deal" },
];

// Total steps — derived for clarity below.
export const LAST_STEP = STEPS.length; // 6

// X1 — Actionable validation. Human-readable label for every field the
// wizard's per-step validator can flag. Keep in sync with the keys used
// in computeStepErrors() in useWizardState.js.
export const FIELD_LABELS = {
  // step 2 — Property
  address: "Address",
  city: "City",
  asking_price: "Asking price",
  units: "Units",
  // step 3 — Income
  monthly_rent: "Monthly rent",
  unit_mix: "Unit mix",
  // step 4 — Expenses
  taxes_annual: "Annual taxes",
  insurance_annual: "Annual insurance",
  hoa_monthly: "HOA (monthly)",
  utilities_monthly: "Utilities (monthly)",
  repairs: "Upfront repairs",
  vacancy_percent: "Vacancy %",
  management_percent: "Management %",
  // step 5 — Financing
  down_payment_percent: "Down payment %",
  interest_rate: "Interest rate %",
  loan_term_years: "Loan term (years)",
  closing_costs: "Closing costs",
};

export function asNumber(v, fallback = 0) {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function NumberField({ id, label, value, onChange, prefix, suffix, step = "any", min, max, testid, hint }) {
  return (
    <div>
      <Label htmlFor={id} className="text-slate-700">{label}</Label>
      <div className="relative mt-1.5">
        {prefix && (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-slate-400">
            {prefix}
          </span>
        )}
        <Input
          id={id}
          data-testid={testid}
          type="number"
          inputMode="decimal"
          step={step}
          min={min}
          max={max}
          value={value === 0 ? "" : value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          className={`${prefix ? "pl-7" : ""} ${suffix ? "pr-12" : ""}`}
          placeholder="0"
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-400">
            {suffix}
          </span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// Phase 17 — Local Regulations Alert card. Compact, collapsible by default.
// Shows risk badge, AI summary, restrictions, tenant protections, recent changes.
export function RegulationsAlertCard({ city, state, strategy, data, loading }) {
  const [open, setOpen] = useState(false);
  if (loading) {
    return (
      <div
        data-testid="regulations-card"
        data-state="loading"
        className="mt-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Looking up local regulations for {city}, {state}…
      </div>
    );
  }
  if (!data) return null;
  const risk = (data.risk_level || "low").toLowerCase();
  const tone =
    risk === "high"
      ? { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", chip: "bg-rose-600 text-white", Icon: AlertTriangle }
      : risk === "medium"
      ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", chip: "bg-amber-500 text-white", Icon: AlertTriangle }
      : { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", chip: "bg-emerald-600 text-white", Icon: ShieldCheck };
  const stratPretty = strategy === "short_term" ? "Short-Term" : strategy === "mid_term" ? "Mid-Term" : "Long-Term";
  const Lists = ({ title, items, dataTestid }) => {
    if (!items || items.length === 0) return null;
    return (
      <div data-testid={dataTestid} className="mt-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</p>
        <ul className="mt-1 space-y-1 text-sm text-slate-700">
          {items.map((it, i) => (
            <li key={`${title}-${i}-${String(it).slice(0, 32)}`} className="flex gap-2">
              <span className="mt-1 inline-block h-1.5 w-1.5 flex-none rounded-full bg-slate-400" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  return (
    <div
      data-testid="regulations-card"
      data-risk={risk}
      data-state="loaded"
      className={`mt-3 rounded-2xl border-2 ${tone.border} ${tone.bg} px-4 py-3 shadow-sm`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ScrollText className={`h-4 w-4 ${tone.text}`} />
          <p className={`text-sm font-bold ${tone.text}`}>
            Local Regulations Alert — {city}, {state}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="regulations-risk-chip"
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone.chip}`}
          >
            <tone.Icon className="h-3 w-3" /> {risk} risk
          </span>
          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600">
            {stratPretty}
          </span>
          {data.requires_permit ? (
            <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
              Permit required
            </span>
          ) : null}
        </div>
      </div>

      {data.summary_md ? (
        <p className="mt-2 whitespace-pre-line text-sm text-slate-700">
          {data.summary_md}
        </p>
      ) : null}

      {(data.restrictions?.length || data.tenant_protections?.length || data.recent_changes?.length) ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="regulations-toggle-details"
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"
            aria-expanded={open}
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            {open ? "Hide details" : "Show rules, protections & recent changes"}
          </button>
          {open ? (
            <div className="mt-1 rounded-lg bg-white/60 px-3 py-2">
              <Lists title="Restrictions" items={data.restrictions} dataTestid="regulations-restrictions" />
              <Lists title="Tenant protections" items={data.tenant_protections} dataTestid="regulations-tenant-protections" />
              <Lists title="Recent changes" items={data.recent_changes} dataTestid="regulations-recent-changes" />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function StepIndicator({ current, onJump, maxReached }) {
  return (
    <ol className="flex items-center" data-testid="wizard-step-indicator">
      {STEPS.map((s, i) => {
        const status = s.id < current ? "complete" : s.id === current ? "current" : "future";
        const clickable = s.id <= maxReached;
        return (
          <li key={s.id} className={`flex flex-1 items-center ${i < STEPS.length - 1 ? "" : "flex-none"}`}>
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onJump(s.id)}
              data-testid={`step-${s.id}`}
              className={`group flex items-center gap-3 ${clickable ? "cursor-pointer" : "cursor-not-allowed"}`}
            >
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition-all
                  ${status === "complete" ? "border-indigo-600 bg-indigo-600 text-white" : ""}
                  ${status === "current" ? "border-indigo-600 bg-white text-indigo-600 ring-4 ring-indigo-100" : ""}
                  ${status === "future" ? "border-slate-200 bg-white text-slate-400" : ""}
                `}
              >
                {status === "complete" ? <Check className="h-4 w-4" /> : s.id}
              </span>
              <span className="hidden text-left sm:block">
                <span className={`block text-xs font-semibold ${status !== "future" ? "text-slate-900" : "text-slate-400"}`}>
                  {s.title}
                </span>
                <span className="block text-[10px] uppercase tracking-wider text-slate-400">{s.description}</span>
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <span className={`mx-2 h-px flex-1 ${s.id < current ? "bg-indigo-600" : "bg-slate-200"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}
